import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageForm from '../../templates/PageForm';
import useFetch from '../../../hooks/useFetch';
import FormSkeleton from '../../atoms/FormSkeleton';
import RuleDetailModal from './RuleDetailModal';
import { FiInfo } from 'react-icons/fi';
import appConfig from '../../../config/app-config';

const API_KEY = appConfig.apis.backofficeApiKey;
const BASE_URL = 'https://7u01x4ndpl.execute-api.us-east-1.amazonaws.com/v1/softwares/analysis';

const gradeColor: Record<string, string> = {
    A: 'bg-green-500', B: 'bg-blue-500', C: 'bg-yellow-500', D: 'bg-orange-500', E: 'bg-red-500',
};

const costBandColor: Record<string, string> = {
    baixo: 'text-green-600', medio: 'text-yellow-600', alto: 'text-orange-600', critico: 'text-red-600',
};

const pillarKeys = ['architecture', 'complexity', 'tests', 'operability', 'security'] as const;
const pillarShort: Record<string, string> = {
    architecture: 'Arq', complexity: 'Compl', tests: 'Testes', operability: 'Ops', security: 'Sec',
};

const pillarScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
};

const SoftwareAnalysisPageView: React.FC = () => {
    const navigate = useNavigate();
    const [selectedRule, setSelectedRule] = useState<string | null>(null);
    const { isLoading, data, error, isTokenError } = useFetch(BASE_URL, [{ key: 'x-api-key', value: API_KEY ?? '' }]);

    if (isTokenError) navigate('/denied');
    if (error) navigate('/network-error');

    const stats = data?.global_stats;
    const ranking = data?.ranking || [];
    const quickWins = data?.quick_wins || [];

    return (
        <PageForm
            pageTitle="Análise de Engenharia"
            apiVersion={data ? `${data.apiName} - ${data.apiVersion}` : ''}
            isLoading={isLoading}
            breadcrumb={[
                { label: 'TI', url: '#' },
                { label: 'Portfólio de Software', url: '/ti/software-portfolio/0/20/name/ASC' },
                { label: 'Análise de Engenharia', url: '#' },
            ]}
            controlSlot={null}
        >
            <div className="p-4 space-y-6">
                {isLoading ? <FormSkeleton /> : data && (
                    <>
                        {/* Stats globais */}
                        <div className="grid grid-cols-5 gap-4">
                            {[
                                { label: 'Projetos', value: data.total_projects },
                                { label: 'Score Médio', value: stats?.avg_score?.toFixed(1) },
                                { label: 'Violações', value: stats?.total_violations },
                                { label: 'Custo Total', value: stats?.total_compliance_cost?.toFixed(1) },
                                { label: 'Regra + Violada', value: stats?.most_violated_rule },
                            ].map((s, i) => (
                                <div key={i} className="bg-white border rounded-lg p-4 text-center shadow-sm">
                                    <p className="text-sm text-gray-500">{s.label}</p>
                                    <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                                </div>
                            ))}
                        </div>

                        {/* Distribuição por grade */}
                        {stats?.projects_by_grade && (
                            <div className="bg-white border rounded-lg p-4 shadow-sm">
                                <h3 className="text-sm font-semibold text-gray-700 mb-3">Distribuição por Grade</h3>
                                <div className="flex gap-3">
                                    {Object.entries(stats.projects_by_grade as Record<string, number>).map(([grade, count]) => (
                                        <div key={grade} className="flex items-center gap-2">
                                            <span className={`${gradeColor[grade]} text-white text-xs font-bold px-2 py-1 rounded`}>{grade}</span>
                                            <span className="text-gray-700 font-medium">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Ranking */}
                        <div className="bg-white border rounded-lg shadow-sm">
                            <h3 className="text-sm font-semibold text-gray-700 p-4 border-b">Ranking de Projetos</h3>
                            <table className="w-full">
                                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                                    <tr>
                                        <th className="px-4 py-2 text-left">#</th>
                                        <th className="px-4 py-2 text-left">Projeto</th>
                                        <th className="px-4 py-2 text-center">Tipo</th>
                                        {pillarKeys.map(k => (
                                            <th key={k} className="px-3 py-2 text-center">{pillarShort[k]}</th>
                                        ))}
                                        <th className="px-4 py-2 text-center">Score</th>
                                        <th className="px-4 py-2 text-center">Grade</th>
                                        <th className="px-4 py-2 text-right">Custo</th>
                                        <th className="px-4 py-2 text-center">Banda</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ranking.map((p: any, i: number) => (
                                        <tr
                                            key={p.name}
                                            className="border-b hover:bg-slate-50 cursor-pointer"
                                            onClick={() => navigate(`/ti/software-analysis/${p.name}`)}
                                        >
                                            <td className="px-4 py-3 text-gray-500">{i + 1}</td>
                                            <td className="px-4 py-3 font-medium text-blue-600 hover:underline">{p.name}</td>
                                            <td className="px-4 py-3 text-center text-xs text-gray-500">{p.structure_type}</td>
                                            {pillarKeys.map(k => (
                                                <td key={k} className={`px-3 py-3 text-center font-mono text-sm ${pillarScoreColor(p.pillars?.[k] ?? 0)}`}>
                                                    {p.pillars?.[k] ?? '-'}
                                                </td>
                                            ))}
                                            <td className="px-4 py-3 text-center font-bold">{p.score}</td>
                                            <td className="px-4 py-3 text-center">
                                                <span className={`${gradeColor[p.grade]} text-white text-xs font-bold px-2 py-1 rounded`}>{p.grade}</span>
                                            </td>
                                            <td className="px-4 py-3 text-right font-mono">{p.compliance_cost?.toFixed(1)}</td>
                                            <td className={`px-4 py-3 text-center font-semibold text-xs ${costBandColor[p.cost_band] || ''}`}>{p.cost_band}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Quick Wins */}
                        {quickWins.length > 0 && (
                            <div className="bg-white border rounded-lg shadow-sm">
                                <h3 className="text-sm font-semibold text-gray-700 p-4 border-b">🎯 Quick Wins</h3>
                                <div className="divide-y">
                                    {quickWins.map((qw: any, i: number) => (
                                        <div key={i} className="px-4 py-3 flex items-center justify-between">
                                            <div>
                                                <span className="font-medium text-gray-900">{qw.project}</span>
                                                <span className="mx-2 text-gray-400">→</span>
                                                <span className="text-sm font-mono text-gray-600">{qw.rule}</span>
                                                <FiInfo className="inline ml-1 text-blue-400 hover:text-blue-600 cursor-pointer" onClick={(e) => { e.stopPropagation(); setSelectedRule(qw.rule); }} />
                                                <span className="ml-2 text-xs text-gray-400">({qw.occurrences} ocorrências em {qw.files?.length} arquivo{qw.files?.length > 1 ? 's' : ''})</span>
                                            </div>
                                            <span className="font-bold text-orange-600">{qw.cost?.toFixed(1)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <p className="text-xs text-gray-400 text-right">Escaneado em: {new Date(data.scan_timestamp).toLocaleString('pt-BR')}</p>
                    </>
                )}
            </div>
            <RuleDetailModal ruleId={selectedRule} show={!!selectedRule} onClose={() => setSelectedRule(null)} />
        </PageForm>
    );
};

export default SoftwareAnalysisPageView;
